import { Eye, Pencil, Trash2, Users, CirclePlus } from 'lucide-react';
import { useEffect, useMemo, useCallback, useState, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import CreateSubgroup from '../CreateSubgroup';
import EditGroup from '../EditGroup';
import EditSubgroup from '../EditSubgroup';
import ViewGroup from '../ViewGroup';
import ViewGroupMembers from '../ViewGroupMembers';
import ViewSubgroup from '../ViewSubgroup';
import { DeleteGroupDocument } from './DeleteGroup.api';
import { DeleteSubgroupDocument } from './DeleteSubgroup.api';
import type { GroupListItemDataFragment, SubgroupListItemDataFragment } from './GroupList.api';
import { useFetchGroupsQuery } from './GroupList.api';
import useGroupListReducer from './useGroupListReducer';
import { GroupColumnKey, getSortField } from './utils';
import client from '@/apolloClient';
import { GroupSortingField as DefaultSortField, SortingOrder as GqlSortingOrder } from '@/apolloClient.types';
import useAppAbilities from '@/components/abilities';
import Empty from '@/components/blocks/Empty';
import Badge from '@/components/common/Badge';
import EmptyContainer from '@/components/common/EmptyContainer';
import IconButton from '@/components/common/IconButton';
import Loader from '@/components/common/Loader';
import Table from '@/components/common/Table';
import {
    SortingOrder,
    type TableProps,
    type SortOrder as TableSortOrderValue,
    type ExpandableConfig,
} from '@/components/common/Table/types';
import TableFooter from '@/components/common/TableFooter';
import { confirm as showConfirm } from '@/utils/ConfirmModal';
import '@/utils/notifications';
import { success as showSuccessNotification, error as showErrorNotification } from '@/utils/notifications/actions';
import useDateFormats from '@/utils/useDateFormats';

interface ListProps {
    searchText?: string;
    companyId: string;
}

export interface GroupListRef {
    refetch: () => void;
}

const pageSizeOptions = [10, 25, 50, 100];

const GroupList = forwardRef<GroupListRef, ListProps>(({ searchText, companyId }, ref) => {
    const { t } = useTranslation('groups');
    const abilities = useAppAbilities();
    const [viewGroupModalOpen, setViewGroupModalOpen] = useState(false);
    const [viewGroupMembers, setViewGroupMembers] = useState(false);
    const [viewSubgroupModalOpen, setViewSubgroupModalOpen] = useState(false);
    const [editGroupModalOpen, setEditGroupModalOpen] = useState(false);
    const [editSubgroupModalOpen, setEditSubgroupModalOpen] = useState(false);
    const [createSubgroupModalOpen, setCreateSubgroupModalOpen] = useState(false);
    const [selectedGroup, setSelectedGroup] = useState<GroupListItemDataFragment | undefined>(undefined);
    const [selectedSubgroup, setSelectedSubgroup] = useState<SubgroupListItemDataFragment | undefined>(undefined);

    const [state, dispatch] = useGroupListReducer();
    const { page, pageSize, sort, filter } = state;
    const { formatDateFullWithTime } = useDateFormats();
    const { loading, data, error, refetch } = useFetchGroupsQuery({
        fetchPolicy: 'cache-and-network',
        variables: {
            companyId,
            pagination: {
                limit: pageSize,
                offset: (page - 1) * pageSize,
            },
            sort: { field: sort.field, order: sort.order },
            filter: filter,
        },
        skip: !companyId,
    });

    useImperativeHandle(
        ref,
        () => ({
            refetch,
        }),
        [refetch]
    );

    useEffect(() => {
        dispatch({
            type: 'setFilter',
            filter: {
                searchText: searchText?.trim() || undefined,
            },
        });
    }, [searchText, dispatch]);

    const handleSortChange = useCallback(
        (columnKey?: string, order?: TableSortOrderValue) => {
            if (!columnKey || !order) {
                dispatch({
                    type: 'setSort',
                    sortBy: {
                        field: DefaultSortField.Name,
                        order: GqlSortingOrder.Asc,
                        orderValue: SortingOrder.Asc,
                    },
                });
                return;
            }

            const groupColumnKey = columnKey as GroupColumnKey;
            const field = getSortField(groupColumnKey);

            if (field) {
                dispatch({
                    type: 'setSort',
                    sortBy: {
                        field: field,
                        order: order === SortingOrder.Asc ? GqlSortingOrder.Asc : GqlSortingOrder.Desc,
                        orderValue: order,
                    },
                });
            } else {
                dispatch({
                    type: 'setSort',
                    sortBy: {
                        field: DefaultSortField.Name,
                        order: GqlSortingOrder.Asc,
                        orderValue: SortingOrder.Asc,
                    },
                });
            }
        },
        [dispatch]
    );

    const handleEditGroup = useCallback((record: GroupListItemDataFragment) => {
        setSelectedGroup(record);
        setEditGroupModalOpen(true);
    }, []);

    const closeEditGroupModal = useCallback(() => {
        setSelectedGroup(undefined);
        setEditGroupModalOpen(false);
    }, []);

    const handleViewGroup = useCallback((record: GroupListItemDataFragment) => {
        setSelectedGroup(record);
        setViewGroupModalOpen(true);
    }, []);

    const handleViewGroupMembers = useCallback((record: GroupListItemDataFragment) => {
        setSelectedGroup(record);
        setSelectedSubgroup(undefined);
        setViewGroupMembers(true);
    }, []);
    const handleViewSubgroupMembers = useCallback(
        (record: SubgroupListItemDataFragment) => {
            const group = data?.groups.items.find(group => group.id === record.parentGroupId);
            if (!group) {
                return;
            }
            setSelectedGroup(group);
            setSelectedSubgroup(record);
            setViewGroupMembers(true);
        },
        [data]
    );
    const closeViewGroupModal = useCallback(() => {
        setViewGroupModalOpen(false);
        setSelectedGroup(undefined);
    }, []);

    const handleViewSubgroup = useCallback(
        (record: SubgroupListItemDataFragment) => {
            const group = data?.groups.items.find(group => group.id === record.parentGroupId);
            if (!group) {
                return;
            }
            setSelectedGroup(group);
            setSelectedSubgroup(record);
            setViewSubgroupModalOpen(true);
        },
        [data]
    );

    const closeViewGroupMembers = useCallback(() => {
        setViewGroupMembers(false);
        setSelectedGroup(undefined);
    }, []);

    const closeViewSubgroupModal = useCallback(() => {
        setViewSubgroupModalOpen(false);
        setSelectedGroup(undefined);
        setSelectedSubgroup(undefined);
    }, []);

    const handleCreateSubgroup = useCallback((record: GroupListItemDataFragment) => {
        setSelectedGroup(record);
        setCreateSubgroupModalOpen(true);
    }, []);

    const handleEditSubgroup = useCallback(
        (record: SubgroupListItemDataFragment) => {
            const group = data?.groups.items.find(group => group.id === record.parentGroupId);
            if (!group) {
                return;
            }
            setSelectedGroup(group);
            setSelectedSubgroup(record);
            setEditSubgroupModalOpen(true);
        },
        [data]
    );

    const closeEditSubgroupModal = useCallback(() => {
        setSelectedGroup(undefined);
        setSelectedSubgroup(undefined);
        setEditSubgroupModalOpen(false);
    }, []);

    const handleDeleteGroup = useCallback(
        (record: GroupListItemDataFragment) => {
            showConfirm(
                {
                    title: t('deleteGroupModal.title', { name: record.name }),
                    content: t('deleteGroupModal.content'),
                    isDeletion: true,
                },
                async () => {
                    try {
                        await client.mutate({
                            mutation: DeleteGroupDocument,
                            variables: { id: record.id, companyId: companyId },
                            refetchQueries: ['FetchGroups'],
                        });
                        showSuccessNotification({
                            message: t('deleteGroupModal.notifications.successTitle'),
                            description: t('deleteGroupModal.notifications.successDescription', {
                                name: record.name,
                            }),
                        });
                        refetch();
                    } catch {
                        showErrorNotification({
                            message: t('deleteGroupModal.notifications.errorTitle'),
                            description: t('deleteGroupModal.notifications.errorDescription'),
                        });
                    }
                }
            );
        },
        [companyId, refetch, t]
    );

    const handleDeleteSubgroup = useCallback(
        (record: SubgroupListItemDataFragment) => {
            showConfirm(
                {
                    title: t('deleteSubgroupModal.title', { name: record.name }),
                    content: t('deleteSubgroupModal.content', { count: record.memberCount || 0 }),
                    isDeletion: true,
                },
                async () => {
                    try {
                        await client.mutate({
                            mutation: DeleteSubgroupDocument,
                            variables: { id: record.id, companyId: companyId },
                            refetchQueries: ['FetchGroups'],
                        });
                        showSuccessNotification({
                            message: t('deleteSubgroupModal.notifications.successTitle'),
                            description: t('deleteSubgroupModal.notifications.successDescription', {
                                name: record.name,
                            }),
                        });
                        refetch();
                    } catch {
                        showErrorNotification({
                            message: t('deleteSubgroupModal.notifications.errorTitle'),
                            description: t('deleteSubgroupModal.notifications.errorDescription'),
                        });
                    }
                }
            );
        },
        [companyId, refetch, t]
    );

    const handlePageChange = useCallback(
        (newPage: number) => {
            dispatch({ type: 'setPage', page: newPage });
        },
        [dispatch]
    );

    const handlePageSizeChange = useCallback(
        (newPageSize: number) => {
            dispatch({ type: 'setPageSize', pageSize: newPageSize });
        },
        [dispatch]
    );

    const columns = useMemo(
        (): TableProps['columns'] => [
            {
                title: t('groupList.tableHeaders.name'),
                dataIndex: 'name',
                key: GroupColumnKey.Name,
                className: 'flex-1',
                sorter: true,
                sortOrder: sort.field === DefaultSortField.Name ? sort.orderValue : undefined,
            },
            {
                title: t('groupList.tableHeaders.description'),
                dataIndex: 'description',
                key: GroupColumnKey.Description,
                className: 'flex-1',
                sorter: true,
                sortOrder: sort.field === DefaultSortField.Description ? sort.orderValue : undefined,
            },
            {
                title: t('groupList.tableHeaders.members'),
                dataIndex: 'totalMemberCount',
                key: GroupColumnKey.TotalMemberCount,
                className: 'flex-1',
                render: (total = 0, record) =>
                    abilities.canManageGroupMembers() ? (
                        <button
                            type="button"
                            onClick={total > 0 ? () => handleViewGroupMembers(record) : undefined}
                            className={total > 0 ? 'cursor-pointer' : 'pointer-events-none cursor-default'}
                            tabIndex={total > 0 ? 0 : -1}
                            aria-label={t('groupList.tableHeaders.members')}
                            aria-disabled={total === 0}
                        >
                            <Badge
                                text={total}
                                icon={<Users className="h-4 w-4" />}
                                color={total > 0 ? 'teal' : 'gray'}
                                style="shadow"
                            />
                        </button>
                    ) : null,
                sorter: true,
                sortOrder: sort.field === DefaultSortField.MemberCount ? sort.orderValue : undefined,
            },
            {
                title: t('groupList.tableHeaders.lastUpdated'),
                dataIndex: 'updatedAt',
                key: GroupColumnKey.UpdatedAt,
                className: 'flex-1',
                render: gr => formatDateFullWithTime(gr),
                sorter: true,
                sortOrder: sort.field === DefaultSortField.UpdatedAt ? sort.orderValue : undefined,
            },
            {
                title: <div className="text-right">{t('groupList.tableHeaders.actions')}</div>,
                dataIndex: 'id',
                key: 'actions',
                className: 'w-32',
                render: (_, record) => {
                    return (
                        <div className="flex items-center justify-end gap-5">
                            {abilities.canCreateGroups() && (
                                <IconButton
                                    onClick={() => handleCreateSubgroup(record)}
                                    title={t('groupList.actions.createSubgroup')}
                                    icon={<CirclePlus className="h-4 w-4 text-green-500" strokeWidth={2} />}
                                />
                            )}
                            {abilities.canViewGroups() && (
                                <IconButton
                                    onClick={() => handleViewGroup(record)}
                                    title={t('groupList.actions.view')}
                                    icon={<Eye className="h-4 w-4 text-blue-600" strokeWidth={2} />}
                                />
                            )}
                            {abilities.canEditGroups() && (
                                <IconButton
                                    onClick={() => handleEditGroup(record)}
                                    title={t('groupList.actions.edit')}
                                    icon={<Pencil className="h-4 w-4 text-yellow-500" strokeWidth={2} />}
                                />
                            )}
                            {abilities.canDeleteGroups() && (
                                <IconButton
                                    onClick={() => handleDeleteGroup(record)}
                                    title={t('groupList.actions.delete')}
                                    icon={<Trash2 className="h-4 w-4 text-red-500" strokeWidth={2} />}
                                />
                            )}
                        </div>
                    );
                },
            },
        ],
        [
            abilities,
            formatDateFullWithTime,
            handleCreateSubgroup,
            handleDeleteGroup,
            handleEditGroup,
            handleViewGroup,
            handleViewGroupMembers,
            sort.field,
            sort.orderValue,
            t,
        ]
    );

    const subColumns = useMemo(
        (): TableProps['columns'] => [
            {
                title: t('groupList.tableHeaders.name'),
                dataIndex: 'name',
                key: GroupColumnKey.Name,
                className: 'flex-1',
            },
            {
                title: t('groupList.tableHeaders.description'),
                dataIndex: 'description',
                key: GroupColumnKey.Description,
                className: 'flex-1',
            },
            {
                title: t('groupList.tableHeaders.members'),
                dataIndex: 'memberCount',
                key: GroupColumnKey.TotalMemberCount,
                className: 'flex-1',
                render: (total = 0, record) =>
                    abilities.canManageGroupMembers() ? (
                        <button
                            type="button"
                            className={total > 0 ? 'cursor-pointer' : 'pointer-events-none cursor-default'}
                            onClick={total > 0 ? () => handleViewSubgroupMembers(record) : undefined}
                            tabIndex={total > 0 ? 0 : -1}
                            aria-label={t('groupList.tableHeaders.members')}
                            aria-disabled={total === 0}
                        >
                            <Badge
                                text={total}
                                icon={<Users className="h-4 w-4" />}
                                color={total > 0 ? 'teal' : 'gray'}
                                style="shadow"
                            />
                        </button>
                    ) : null,
            },
            {
                title: t('groupList.tableHeaders.lastUpdated'),
                dataIndex: 'updatedAt',
                key: GroupColumnKey.UpdatedAt,
                render: gr => formatDateFullWithTime(gr),
                className: 'flex-1',
            },
            {
                title: <div className="w-32 text-right">{t('groupList.tableHeaders.actions')}</div>,
                dataIndex: 'id',
                key: 'actions',
                className: 'w-32',
                render: (_, record) => {
                    return (
                        <div className="flex items-center justify-end gap-5">
                            <div className="w-4" />
                            {abilities.canViewGroups() && (
                                <IconButton
                                    onClick={() => handleViewSubgroup(record)}
                                    title={t('groupList.actions.view')}
                                    icon={<Eye className="h-4 w-4 text-blue-600" strokeWidth={2} />}
                                />
                            )}
                            {abilities.canCreateGroups() && (
                                <IconButton
                                    onClick={() => handleEditSubgroup(record)}
                                    title={t('groupList.actions.edit')}
                                    icon={<Pencil className="h-4 w-4 text-yellow-500" strokeWidth={2} />}
                                />
                            )}
                            {abilities.canDeleteGroups() && (
                                <IconButton
                                    onClick={() => handleDeleteSubgroup(record)}
                                    title={t('groupList.actions.delete')}
                                    icon={<Trash2 className="h-4 w-4 text-red-500" strokeWidth={2} />}
                                />
                            )}
                        </div>
                    );
                },
            },
        ],
        [
            abilities,
            formatDateFullWithTime,
            handleDeleteSubgroup,
            handleEditSubgroup,
            handleViewSubgroup,
            handleViewSubgroupMembers,
            t,
        ]
    );
    const expandable: ExpandableConfig<any> = {
        childrenField: 'subgroups',
        childColumns: subColumns,
    };
    const totalCount = data?.groups?.count ?? 0;

    if (error) {
        return (
            <EmptyContainer>
                <Empty title={t('groupList.empty.title')} />
            </EmptyContainer>
        );
    }

    if (loading && !data?.groups.items.length) {
        return <Loader />;
    }

    if (!data?.groups.items.length && !loading) {
        return (
            <EmptyContainer>
                <Empty title={t('groupList.empty.title')} />
            </EmptyContainer>
        );
    }

    return (
        <>
            <div className="flex flex-col gap-5">
                <Table
                    columns={columns}
                    expandable={expandable}
                    dataSource={data?.groups.items}
                    loading={loading}
                    onSortChange={handleSortChange}
                    rowKey="id"
                />

                {totalCount > 0 && (
                    <TableFooter
                        currentPage={page}
                        pageSize={pageSize}
                        totalCount={totalCount}
                        pageSizeOptions={pageSizeOptions}
                        onPageChange={handlePageChange}
                        onPageSizeChange={handlePageSizeChange}
                    />
                )}
            </div>

            <CreateSubgroup
                open={createSubgroupModalOpen}
                companyId={companyId}
                parentGroup={selectedGroup || null}
                onClose={() => {
                    setCreateSubgroupModalOpen(false);
                }}
                onCreated={() => {
                    setCreateSubgroupModalOpen(false);
                    refetch();
                }}
            />

            <EditGroup
                group={selectedGroup}
                companyId={companyId}
                onCreated={() => {
                    setEditGroupModalOpen(false);
                    refetch();
                }}
                open={editGroupModalOpen}
                onClose={closeEditGroupModal}
            />

            <EditSubgroup
                parentGroup={selectedGroup || null}
                subgroup={selectedSubgroup || null}
                companyId={companyId}
                onCreated={() => {
                    setEditSubgroupModalOpen(false);
                    refetch();
                }}
                open={editSubgroupModalOpen}
                onClose={closeEditSubgroupModal}
            />

            <ViewGroup group={selectedGroup} open={viewGroupModalOpen} onClose={closeViewGroupModal} />

            <ViewSubgroup
                group={selectedGroup}
                subgroup={selectedSubgroup}
                open={viewSubgroupModalOpen}
                onClose={closeViewSubgroupModal}
            />

            <ViewGroupMembers
                group={selectedGroup}
                subgroup={selectedSubgroup}
                open={viewGroupMembers}
                onClose={closeViewGroupMembers}
            />
        </>
    );
});

GroupList.displayName = 'GroupList';

export default GroupList;
